import React, { useContext } from 'react';

import { Redirection } from '@confluence/route-manager/entry-points/Redirection';
import { SPACE_OVERVIEW as SPACE_OVERVIEW_MARKER } from '@confluence/screen';
import { SpaceOverviewLoader } from '@confluence/space-overview';
import { PRODUCT_HOME_ID } from '@confluence/app-navigation/entry-points/navigationIDs';
import { useIsCompanyHubEnabled } from '@confluence/app-navigation/entry-points/useIsCompanyHubEnabled';
import { getCompanyHubSpaceKey } from '@confluence/route-manager/entry-points/companyHubUtils';
import { HOME, COMPANY_HUB } from '@confluence/named-routes';
import { START_TOUCH } from '@confluence/navdex';
import { useCompanyHubPremiumGate } from '@confluence/company-hub/entry-points/useCompanyHubPremiumGate';
import { CompanyHomeBuilderUpsellPage } from '@confluence/change-edition/entry-points/CompanyHomeBuilderUpsellPage';
import { SPAViewContext } from '@confluence/spa-view-context';

export function getCompanyHubRoute(Component: React.ElementType) {
	const CompanyHubRoute = () => {
		const spaceKey = getCompanyHubSpaceKey() || '';
		const shouldShowPremiumUpsell = useCompanyHubPremiumGate();
		const { isCompanyHubEntryPointEnabled } = useIsCompanyHubEnabled();
		const { isSiteAdmin, loading } = useContext(SPAViewContext);

		if (shouldShowPremiumUpsell && !loading) {
			if (isSiteAdmin) {
				// CompanyHomeBuilderUpsellPage was supposed to be rendered here, but it's
				// built on top of the underlying change-edition component
				// UpsellLooknFeel/SectionMessage which doesn't support SSR. Since the
				// SectionMessage is at the top of the content area of
				// CompanyHomeBuilderUpsellPage, even if the rest of the content area were
				// SSRed, it would shift vertically when the SPA loads. But the top nav is
				// still useful.
				return process.env.REACT_SSR ? null : (
					<CompanyHomeBuilderUpsellPage featureName="companyHub" />
				);
			} else {
				return <Redirection name={HOME.name} />;
			}
		}

		if (!loading && !isCompanyHubEntryPointEnabled) {
			return <Redirection name={HOME.name} />;
		}
		// The only different between SPA and SSR is we don't render space base in SSR
		return <Component spaceKey={spaceKey} contentId="" />;
	};

	CompanyHubRoute.SPACE_NAV_PARAMS = () => {
		// eslint-disable-next-line react-hooks/rules-of-hooks
		const { isCompanyHubPublished } = useIsCompanyHubEnabled();
		const spaceKey = getCompanyHubSpaceKey() || '';
		return {
			Screen: {
				screenEvent: {
					name: 'companyHub',
					id: spaceKey,
					attributes: {
						navdexPointType: START_TOUCH,
						publishedCompanyHub: isCompanyHubPublished,
					},
				},
				pageState: {
					spaceKey,
					contentId: SPACE_OVERVIEW_MARKER,
					routeName: COMPANY_HUB.name,
				},
			},
			MainLayout: {
				navView: PRODUCT_HOME_ID,
				spaceKey,
				isViewPage: true,
			},
		};
	};

	return CompanyHubRoute;
}

export const CompanyHubRouteForSSR = getCompanyHubRoute(SpaceOverviewLoader);
